import { useState, type TouchEvent } from "react";

interface GalleryCarouselProps {
  images: string[];
  title: string;
}

export default function GalleryCarousel({ images, title }: GalleryCarouselProps) {
  const [index, setIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);

  if (images.length === 0) return null;

  const go = (next: number) => setIndex((next + images.length) % images.length);

  const onTouchStart = (e: TouchEvent) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e: TouchEvent) => {
    if (touchStart === null) return;
    const delta = e.changedTouches[0].clientX - touchStart;
    if (Math.abs(delta) > 40) go(delta < 0 ? index + 1 : index - 1);
    setTouchStart(null);
  };

  return (
    <div>
      <div
        className="relative w-full overflow-hidden rounded-lg bg-foreground-900"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="relative w-full pt-[56.25%]">
          {images.map((image, i) => (
            <img
              key={image}
              src={image}
              alt={`${title} 剧照 ${i + 1} - 佐佐木明希影视作品`}
              title={`${title} 佐佐木明希 剧照`}
              className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-500 ${
                i === index ? "opacity-100" : "opacity-0"
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一张剧照"
          className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/50 text-background-50 transition-colors hover:bg-foreground-950/75 md:left-5"
        >
          <i className="ri-arrow-left-s-line text-xl" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一张剧照"
          className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/50 text-background-50 transition-colors hover:bg-foreground-950/75 md:right-5"
        >
          <i className="ri-arrow-right-s-line text-xl" aria-hidden="true" />
        </button>

        <span className="absolute bottom-3 right-3 rounded-full bg-foreground-950/60 px-3 py-1 text-xs text-background-50">
          {index + 1} / {images.length}
        </span>
      </div>

      <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
        {images.map((image, i) => (
          <button
            key={image}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`查看第 ${i + 1} 张剧照`}
            className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-md border-2 transition-colors ${
              i === index ? "border-primary-500" : "border-transparent opacity-70 hover:opacity-100"
            }`}
          >
            <img
              src={image}
              alt={`${title} 剧照缩略图 ${i + 1}`}
              className="h-full w-full object-cover object-top"
            />
          </button>
        ))}
      </div>
    </div>
  );
}